// What the server's refusal codes mean, in words a person can act on.
//
// ★ THE SERVER OWNS THE RULE; THIS OWNS THE SENTENCE. Every string below
// corresponds to a code the backend returns deliberately, and the UI must never
// re-implement the rule to guess at one — a screen that decides for itself when
// a campaign may be approved will disagree with the server on the day it
// matters. So: one place, one sentence per code, and an honest fallback.
//
// ★ NEVER "TRY AGAIN" FOR SOMETHING RETRYING CANNOT FIX. A payment block or a
// locked account needs an administrator in the Meta account; telling an agent to
// retry sends them round a loop with no exit. That lesson came from a live test
// where exactly this text was shown for a permanent failure.

const MESSAGES = {
	CAMPAIGN_DELETE_NOT_ALLOWED:
	'Only unstarted campaigns can be deleted. Preparing, active and completed campaigns cannot be deleted.',

CAMPAIGN_ALREADY_STARTED:
	'This campaign has already been prepared or started. Keep it for reporting; use Cancel to stop remaining sends when available.',

CAMPAIGN_DELETE_HAS_ACTIVITY:
	'This campaign has sending activity and cannot be deleted. Its delivery history must be kept.',

CAMPAIGN_ID_INVALID:
	'The campaign ID is invalid. Return to the campaign list and try again.',
	PLANNER_UNAVAILABLE: 'Campaign preparation is temporarily unavailable. Ask an administrator to check the campaign worker.',
	PLANNER_QUEUE_FAILED: 'The preparation job could not be confirmed. Review the refreshed campaign status before taking another action.',
	CAMPAIGN_AGENDA_DISABLED: 'Campaign sending is disabled for this environment.',
	PLAN_JOB_FAILED: 'The preparation job failed. Review the campaign details for the recorded reason.',

	// ─ Lifecycle ─
	CAMPAIGN_NOT_FOUND: 'This campaign no longer exists. It may have been deleted.',
	NOT_EDITABLE: 'This campaign has already been submitted, so its setup is locked. Duplicate it to make changes.',
	STATE_CHANGED: 'Someone else changed this campaign a moment ago. Reload to see where it is now.',
	ILLEGAL_TRANSITION: 'That is not a move this campaign can make from where it is.',
	NOT_PENDING: 'This campaign is not waiting for approval.',
	NOT_PAUSED: 'This campaign is not paused.',
	NOT_SCHEDULED: 'This campaign is not ready to start yet.',
	ALREADY_PLANNED: 'This campaign has already been built and started.',
	APPROVER_IS_CREATOR: 'A campaign cannot be approved by the person who created it. Ask a colleague to review it.',
	REASON_REQUIRED: 'Please give a reason — the person who created this will see it.',

	// ─ Setup problems the Review step surfaces ─
	NAME_REQUIRED: 'Give the campaign a name.',
	INVALID_CAMPAIGN: 'Some things still need fixing before this can be sent.',
	NO_VARIANT: 'Choose a message template first.',
	SCHEDULE_START_INVALID: 'Choose a start date.',
	SCHEDULE_END_INVALID: 'Choose an end date.',
	SCHEDULE_END_BEFORE_START: 'The end date is before the start date.',
	WINDOW_INVALID: 'The sending window needs a start and end time.',
	WINDOW_ENDS_BEFORE_IT_STARTS: 'The sending window ends before it begins.',
	ALL_WEEKDAYS_EXCLUDED: 'Every day of the week is excluded, so this could never send.',
	TIMEZONE_UNKNOWN: 'That time zone is not one we recognise.',
	ATTESTATION_REQUIRED: 'Confirm that these contacts agreed to be messaged before uploading.',
	PHONE_COLUMN_REQUIRED: 'Tell us which column holds the phone number.',
	FILE_REQUIRED: 'Choose a file to upload.',

	// ─ Template problems ─
	TEMPLATE_NOT_FOUND: 'That template is no longer in your WhatsApp account.',
	TEMPLATE_REJECTED: 'WhatsApp rejected this template, so it cannot be sent.',
	TEMPLATE_PAUSED: 'WhatsApp has paused this template. It cannot be sent until they lift it.',
	TEMPLATE_DISABLED: 'WhatsApp has disabled this template.',
	TEMPLATE_PENDING: 'This template is still waiting for WhatsApp to approve it.',

	// ─ Access ─
	FORBIDDEN: 'You do not have permission to do that.',
	PERMISSION_DENIED: 'You do not have permission to do that.',
	CONVERSATION_UNCLAIMED: 'Take this conversation first, then you can reply.',
	NOT_CONVERSATION_OWNER: 'Someone else is handling this conversation.',

	// ─ Things only an administrator can fix ─
	SEND_UNAVAILABLE: 'The WhatsApp service is not reachable right now. Nothing was sent.',
	CONNECTION_LAYER_UNCONFIGURED: 'WhatsApp is not connected yet. Ask an administrator.',
	CONNECTION_LAYER_UNREACHABLE: 'The WhatsApp service is not reachable right now.',
	NO_ACTIVE_CREDENTIAL: 'The WhatsApp connection needs re-authenticating. Ask an administrator.',
	CHANNEL_NOT_SENDABLE: 'This number cannot send at the moment.',
};

/** The one sentence shown when nothing more specific is known. */
export const DEFAULT_MESSAGE = 'That did not work. Please try again, or tell an administrator if it keeps happening.';

/**
 * Turn an RTK Query error — or a bare code — into something worth reading.
 *
 * Handles the three shapes a failure actually arrives in: an RTKQ error with a
 * `data` body, a plain object from the `fetch` helpers, and a bare string code.
 */
export function campaignError(error) {
	if (!error) return null;
	if (Number(error?.status) === 403) return 'You do not have permission for this action. Ask your administrator to check your WhatsApp campaign permissions.';
	if (typeof error === 'string') return MESSAGES[error] ?? DEFAULT_MESSAGE;

	const body = error.data ?? error.body ?? error;
	const code = body?.error ?? body?.code ?? null;

	// A validation failure lists what is wrong; show the FIRST thing to fix
	// rather than a comma-separated wall the reader has to parse.
	if (code === 'INVALID_CAMPAIGN' && body?.details?.length) {
		const first = String(body.details[0]).split(':')[0].split('@')[0];
		return MESSAGES[first] ?? `${MESSAGES.INVALID_CAMPAIGN} (${body.details[0]})`;
	}

	if (code && MESSAGES[code]) return MESSAGES[code];
	// The server's own sentence beats ours when it wrote one for this case.
	if (body?.message && typeof body.message === 'string') return body.message;
	if (error.status === 'FETCH_ERROR') return 'We could not reach the server. Check your connection.';
	return DEFAULT_MESSAGE;
}

/**
 * Why a campaign was paused, for the list and the dashboard banner.
 *
 * `no_capacity` reads as a warning rather than a failure on purpose: it clears
 * itself the moment a number recovers, and telling somebody to act would waste
 * their time.
 */
export const PAUSE_REASON = {
	fail_rate: { tone: 'danger', text: 'Paused automatically — too many messages were failing.' },
	opt_out_rate: { tone: 'danger', text: 'Paused automatically — too many people opted out.' },
	no_capacity: { tone: 'warning', text: 'Waiting for a sender to become available. It resumes on its own.' },
	no_template: { tone: 'danger', text: 'Paused — no approved template is left to send.' },
	paused_by_user: { tone: 'muted', text: 'Paused by hand.' },
};

export function pauseReason(reason) {
	return PAUSE_REASON[reason] ?? (reason ? { tone: 'muted', text: reason } : null);
}

export default campaignError;
